// ─────────────────────────────────────────────────────────────────
// Glass-i — Supplier Payment Register page (Stage 6P.7).
//
// Server-paginated register at /erp/supplier-payments/register.
//
// Columns:
//   Payment No · Payment Date · Supplier (+ GSTIN tag) · Method
//   Reference No · Amount · Voucher No · Status · Posted At
//
// Toolbar:
//   Search (q over payment_no / reference_no / supplier_name / GSTIN)
//   Supplier search · Date range (payment_date) · Status filter
//   Payment method filter · Clear · Refresh
//
// Row interactions:
//   • Double-click → navigate to /erp/supplier-payments/:id
//   • Payment No   → same navigation
//
// Backend remains the authority for ALL payment math:
//   ledger code resolution, supplier-payable balance, voucher
//   posting integration, duplicate-block decisions. This page is a
//   pure read surface — it never mutates payments and never computes
//   balances locally.
//
// Style:
//   Mirrors PurchaseAccountingPostingsPage — slate-50 header strip,
//   22 px row density, sticky toolbar. Reuses the shared
//   GoodsReceiptsPage.css via the .grn-* wrapper so no new CSS file
//   is added.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, DatePicker, Input, Select,
  Space, Spin, Table, Tag, Tooltip, message
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, BankOutlined
} from '@ant-design/icons';

import { supplierPaymentApi } from './supplierPaymentApi';
import {
  STATUSES,
  PAYMENT_METHODS,
  paymentStatusLabel,
  paymentStatusColor,
  paymentMethodLabel
} from './supplierPaymentHelpers';
import useAuth from '../../../hooks/useAuth';
import '../purchase/GoodsReceiptsPage.css';

const { RangePicker } = DatePicker;


// ─────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS  = ['25', '50', '100'];
const DATE_FMT           = 'DD-MMM-YYYY';
const ISO_FMT            = 'YYYY-MM-DD';
const SEARCH_DEBOUNCE_MS = 250;

const STATUS_OPTIONS = STATUSES.map((v) => ({
  value: v,
  label: paymentStatusLabel(v)
}));

const METHOD_OPTIONS = PAYMENT_METHODS.map((v) => ({
  value: v,
  label: paymentMethodLabel(v)
}));


// ─────────────────────────────────────────────────────────────────
// Pure helpers — kept local so the page doesn't depend on a shared
// formatter module that could drift.
// ─────────────────────────────────────────────────────────────────

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day   = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year  = d.getFullYear();
  return `${day}-${month}-${year}`;
}

function fmtDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day   = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year  = d.getFullYear();
  const hh    = String(d.getHours()).padStart(2, '0');
  const mm    = String(d.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${hh}:${mm}`;
}

// ₹ grouping is locale-aware (en-IN) so 12,34,567.89 is rendered the
// same way as elsewhere on the control plane. Falsy / non-finite
// inputs collapse to an em-dash so empty rows don't read as "₹0".
function fmtMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  try {
    return n.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  } catch {
    return n.toFixed(2);
  }
}

function pickField(row, snake, camel) {
  if (!row) return undefined;
  if (camel in row && row[camel] !== undefined && row[camel] !== null) return row[camel];
  if (snake in row && row[snake] !== undefined && row[snake] !== null) return row[snake];
  return undefined;
}

function errMessage(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

// Reference identifier — register endpoint may surface reference_no,
// cheque_no, or utr_no depending on method. Pick the first populated
// one so the register column reads consistently across methods.
function readReferenceNo(row) {
  return (
    pickField(row, 'reference_no', 'referenceNo')
    || pickField(row, 'cheque_no',  'chequeNo')
    || pickField(row, 'utr_no',     'utrNo')
    || pickField(row, 'bank_reference', 'bankReference')
    || pickField(row, 'instrument_no',  'instrumentNo')
    || null
  );
}


// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export default function SupplierPaymentRegisterPage() {
  const navigate = useNavigate();
  const { me } = useAuth();

  // Wildcard '*' is the master role; explicit
  // `erp.supplier_payments.*` strings are the Stage 6P.7 perms.
  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set  = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);

  const canView         = hasPerm('erp.supplier_payments.view');
  const canViewReports  = hasPerm('erp.supplier_payments.reports.view');

  // ── Toolbar state ────────────────────────────────────────────
  const [searchInput,   setSearchInput]   = useState('');
  const [search,        setSearch]        = useState('');
  const [supplierInput, setSupplierInput] = useState('');
  const [supplier,      setSupplier]      = useState('');
  const [status,        setStatus]        = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [dateRange,     setDateRange]     = useState(null);

  // ── Server pagination state ──────────────────────────────────
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total,    setTotal]    = useState(0);

  // ── Data state ───────────────────────────────────────────────
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);

  // ── Debounced search wiring ──────────────────────────────────
  // Keep the visible input snappy, but only re-fetch once the
  // operator pauses typing — same pattern as the postings list.
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch((prev) => {
        if (prev === searchInput) return prev;
        setPage(1);
        return searchInput;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const supplierDebounceRef = useRef(null);
  useEffect(() => {
    if (supplierDebounceRef.current) clearTimeout(supplierDebounceRef.current);
    supplierDebounceRef.current = setTimeout(() => {
      setSupplier((prev) => {
        if (prev === supplierInput) return prev;
        setPage(1);
        return supplierInput;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (supplierDebounceRef.current) clearTimeout(supplierDebounceRef.current);
    };
  }, [supplierInput]);

  // ── Load ─────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!canView && !canViewReports) return;
    setLoading(true);
    try {
      const params = { page, pageSize };
      const needle = (search || '').trim();
      // Send under BOTH `q` and `search` — backend tolerates the
      // unused one via stripUnknown:true on its Joi validator.
      if (needle) {
        params.q      = needle;
        params.search = needle;
      }
      const supplierNeedle = (supplier || '').trim();
      if (supplierNeedle) {
        params.supplier     = supplierNeedle;
        params.supplierName = supplierNeedle;
      }
      if (status)        params.status         = status;
      if (paymentMethod) params.paymentMethod  = paymentMethod;
      if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
        params.fromDate = dateRange[0].format
          ? dateRange[0].format(ISO_FMT)
          : new Date(dateRange[0]).toISOString().slice(0, 10);
        params.toDate   = dateRange[1].format
          ? dateRange[1].format(ISO_FMT)
          : new Date(dateRange[1]).toISOString().slice(0, 10);
      }

      const out = await supplierPaymentApi.listRegister(params);
      const list = Array.isArray(out) ? out
        : Array.isArray(out?.rows) ? out.rows
        : Array.isArray(out?.data) ? out.data
        : Array.isArray(out?.items) ? out.items
        : [];
      const count = Array.isArray(out) ? list.length
        : Number(out?.total ?? out?.count ?? list.length) || 0;

      setRows(list);
      setTotal(count);
    } catch (e) {
      message.error({
        content: errMessage(e, 'Failed to load supplier payment register.'),
        key: 'sp-register'
      });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [canView, canViewReports, page, pageSize, search, supplier, status, paymentMethod, dateRange]);

  useEffect(() => { load(); }, [load]);

  // ── Toolbar handlers — any filter change resets page to 1 ────
  const onStatus = (v) => { setStatus(v || null);        setPage(1); };
  const onMethod = (v) => { setPaymentMethod(v || null); setPage(1); };
  const onDates  = (v) => { setDateRange(v || null);     setPage(1); };
  const onReset  = () => {
    setSearchInput('');
    setSearch('');
    setSupplierInput('');
    setSupplier('');
    setStatus(null);
    setPaymentMethod(null);
    setDateRange(null);
    setPage(1);
  };

  // ── Row navigation ───────────────────────────────────────────
  const openView = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    navigate(`/erp/supplier-payments/${encodeURIComponent(id)}`);
  }, [navigate]);

  // ── Columns ──────────────────────────────────────────────────
  const columns = useMemo(() => ([
    {
      title:     'Payment No',
      dataIndex: 'paymentNo',
      width:     170,
      render: (_v, row) => {
        const no = pickField(row, 'payment_no', 'paymentNo') || '—';
        return (
          <button
            type="button"
            className="grn-link"
            onClick={() => openView(row)}
          >
            {no}
          </button>
        );
      }
    },
    {
      title:     'Payment Date',
      dataIndex: 'paymentDate',
      width:     130,
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtDate(pickField(row, 'payment_date', 'paymentDate'))}
        </span>
      )
    },
    {
      title:     'Supplier',
      dataIndex: 'supplierName',
      render: (_v, row) => {
        const name  = pickField(row, 'supplier_name',  'supplierName')  || '—';
        const gstin = pickField(row, 'supplier_gstin', 'supplierGstin');
        return (
          <div className="grn-supplier">
            <Tooltip title={name}>
              <span className="grn-supplier__name">{name}</span>
            </Tooltip>
            {gstin
              ? <Tag color="blue" className="grn-gstin-tag">{gstin}</Tag>
              : null}
          </div>
        );
      }
    },
    {
      title:     'Method',
      dataIndex: 'paymentMethod',
      width:     130,
      render: (_v, row) => {
        const v = pickField(row, 'payment_method', 'paymentMethod');
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tag className="grn-status-tag" color="default">
            {paymentMethodLabel(v) || String(v).replace(/_/g, ' ')}
          </Tag>
        );
      }
    },
    {
      title:     'Reference No',
      dataIndex: 'referenceNo',
      width:     170,
      render: (_v, row) => {
        const ref = readReferenceNo(row);
        if (!ref) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{ref}</span>;
      }
    },
    {
      title:     'Amount',
      dataIndex: 'paymentAmount',
      width:     140,
      align:     'right',
      render: (_v, row) => {
        const amt = pickField(row, 'payment_amount', 'paymentAmount');
        return (
          <span className="grn-mono grn-line-count">{fmtMoney(amt)}</span>
        );
      }
    },
    {
      title:     'Voucher No',
      dataIndex: 'voucherNo',
      width:     150,
      render: (_v, row) => {
        const v = pickField(row, 'voucher_no', 'voucherNo');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Status',
      dataIndex: 'status',
      width:     140,
      render: (_v, row) => {
        const v = pickField(row, 'status', 'status');
        if (!v) return (
          <Tag className="grn-status-tag" color="default">—</Tag>
        );
        return (
          <Tag color={paymentStatusColor(v)} className="grn-status-tag">
            {paymentStatusLabel(v) || String(v).replace(/_/g, ' ')}
          </Tag>
        );
      }
    },
    {
      title:     'Posted At',
      dataIndex: 'postedAt',
      width:     160,
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtDateTime(pickField(row, 'posted_at', 'postedAt'))}
        </span>
      )
    }
  ]), [openView]);

  // ── Render ───────────────────────────────────────────────────
  if (!canView && !canViewReports) {
    return (
      <div className="grn-shell">
        <Alert
          type="warning"
          showIcon
          message="You don't have permission to view the supplier payment register."
          description="Ask your master account to grant erp.supplier_payments.view or erp.supplier_payments.reports.view."
        />
      </div>
    );
  }

  return (
    <div className="grn-shell">
      {/* Header strip */}
      <div className="grn-headstrip">
        <div className="grn-headstrip__lead">
          <span className="grn-headstrip__icon"><BankOutlined /></span>
          <div className="grn-headstrip__text">
            <span className="grn-headstrip__title">Supplier Payment Register</span>
            <span className="grn-headstrip__subtitle">
              All supplier payments — draft, posted, failed, and cancelled.
              Backend owns ledger resolution, payable balance computation,
              duplicate-block decisions, and voucher posting. This surface
              is read-only.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="grn-stat-chip">
            <strong>{total}</strong> total
          </span>
        </Space>
      </div>

      {/* Sticky toolbar */}
      <div className="grn-toolbar">
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search payment no, reference no, supplier…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onPressEnter={(e) => {
            // Flush debounce on Enter.
            setSearchInput(e.target.value);
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ width: 280 }}
        />
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Supplier name / GSTIN…"
          value={supplierInput}
          onChange={(e) => setSupplierInput(e.target.value)}
          onPressEnter={(e) => {
            setSupplierInput(e.target.value);
            setSupplier(e.target.value);
            setPage(1);
          }}
          style={{ width: 220 }}
        />
        <RangePicker
          size="small"
          value={dateRange}
          onChange={onDates}
          format={DATE_FMT}
          allowClear
          placeholder={['From', 'To']}
          style={{ width: 240 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All statuses"
          value={status || undefined}
          onChange={onStatus}
          options={STATUS_OPTIONS}
          style={{ width: 170 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All methods"
          value={paymentMethod || undefined}
          onChange={onMethod}
          options={METHOD_OPTIONS}
          style={{ width: 170 }}
        />
        <Tooltip title="Clear filters">
          <Button size="small" onClick={onReset}>Clear</Button>
        </Tooltip>
        <div className="grn-toolbar__spacer" />
        <Tooltip title="Refresh">
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={load}
          />
        </Tooltip>
      </div>

      {/* Grid card */}
      <Card className="grn-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            className="grn-grid"
            size="small"
            rowKey={(r) =>
              pickField(r, 'id', 'id')
              || pickField(r, 'payment_no', 'paymentNo')}
            dataSource={rows}
            columns={columns}
            sticky
            scroll={{ x: 'max-content' }}
            onRow={(row) => ({
              onDoubleClick: () => openView(row)
            })}
            pagination={{
              current:           page,
              pageSize,
              total,
              showSizeChanger:   true,
              pageSizeOptions:   PAGE_SIZE_OPTIONS,
              showQuickJumper:   total > pageSize,
              size:              'small',
              showTotal: (count, range) =>
                count === 0
                  ? 'No rows'
                  : `${range[0]}–${range[1]} of ${count}`,
              onChange: (nextPage, nextSize) => {
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                } else {
                  setPage(nextPage);
                }
              }
            }}
            locale={{
              emptyText: loading
                ? ' '
                : 'No supplier payments yet.'
            }}
          />
        </Spin>
      </Card>
    </div>
  );
}
